import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils/helpers'
import { Eye, File, RefreshCw, Trash2 } from 'lucide-react'
import { UploadedDocumentFileNameHeader } from '../detail/UploadedDocumentFileNameHeader'
import { ActionButtonSpinner } from '../detail/reimbursementActionButtonLoading'
import { ReclassifyReplaceExistingFileDialog } from './ReclassifyReplaceExistingFileDialog'
import { RejectionReasonModal } from './RejectionReasonModal'
import { DocumentReviewMetadata } from './DocumentReviewMetadata'
import {
  getReimbursementDocumentUploadStatusAccentTextClass,
  getReimbursementDocumentUploadStatusContainerClasses,
} from '../../utils/reimbursementDocumentUploadStatus'

export interface ReclassifyOption {
  value: string
  label: string
  occupied?: boolean
}

export interface DocumentApprovalControlsProps {
  fileName?: string
  fileSize?: number
  uploadedAt?: Date
  uploadedBy?: string
  status?: 'pending' | 'uploaded' | 'approved' | 'incomplete' | 'rejected'
  reviewedAt?: Date
  reviewedBy?: string
  rejectionReason?: string
  reclassifiedFrom?: string
  reclassifiedAt?: Date
  reclassifiedBy?: string
  reclassifyOptions?: ReclassifyOption[]
  fileUrl?: string
  onViewFile?: () => void
  onStatusChange: (status: 'approved' | 'incomplete', reason?: string) => void
  incompleteReasons?: Array<{ label: string; description: string }>
  onReclassify?: (newDocType: string) => void
  onRemoveFile?: () => void
  removeBusy?: boolean
  rejectLabel?: string
  busy?: boolean
  pendingAction?: 'approve' | 'incomplete' | 'reclassify' | null
  disabled?: boolean
}

export function DocumentApprovalControls({
  fileName,
  fileSize,
  uploadedAt,
  uploadedBy,
  status,
  reviewedAt,
  reviewedBy,
  rejectionReason,
  reclassifiedFrom,
  reclassifiedAt,
  reclassifiedBy,
  reclassifyOptions,
  fileUrl,
  onViewFile,
  onStatusChange,
  onReclassify,
  onRemoveFile,
  removeBusy = false,
  rejectLabel = 'Incomplete',
  incompleteReasons,
  busy = false,
  pendingAction = null,
  disabled = false,
}: DocumentApprovalControlsProps) {
  const isIncomplete = status === 'incomplete' || status === 'rejected'
  const isApproved = status === 'approved'
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [showReclassifyMenu, setShowReclassifyMenu] = useState(false)
  const [pendingReclassify, setPendingReclassify] = useState<ReclassifyOption | null>(null)
  const reclassifyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (reclassifyRef.current && !reclassifyRef.current.contains(e.target as Node)) {
        setShowReclassifyMenu(false)
      }
    }
    if (showReclassifyMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showReclassifyMenu])

  const handleRejectSubmit = (reason: string) => {
    onStatusChange('incomplete', reason)
    setShowRejectionModal(false)
  }

  const handleReclassifySelect = (option: ReclassifyOption) => {
    setShowReclassifyMenu(false)
    if (option.occupied) {
      setPendingReclassify(option)
    } else {
      onReclassify?.(option.value)
    }
  }

  const handleConfirmReclassify = () => {
    if (pendingReclassify) {
      onReclassify?.(pendingReclassify.value)
      setPendingReclassify(null)
    }
  }

  const emptyOptions = reclassifyOptions?.filter((o) => !o.occupied) ?? []
  const occupiedOptions = reclassifyOptions?.filter((o) => o.occupied) ?? []
  const canView = Boolean(onViewFile || fileUrl)
  const actionsDisabled = disabled || busy || !fileName
  const approveDisabled = actionsDisabled || isApproved
  const containerStatus = status ?? 'uploaded'
  const containerClasses = getReimbursementDocumentUploadStatusContainerClasses(containerStatus)
  const accentTextClass = getReimbursementDocumentUploadStatusAccentTextClass(containerStatus)

  const handleViewClick = () => {
    if (onViewFile) {
      onViewFile()
      return
    }
    if (fileUrl) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <>
      <div
        className={`flex flex-1 items-start justify-between p-2 max-[511px]:flex-col max-[511px]:items-stretch max-[511px]:gap-2 ${containerClasses}`}
      >
        <div className="min-w-0 flex-1 max-[511px]:w-full">
          <UploadedDocumentFileNameHeader fileName={fileName ?? ''} />
          {fileSize && fileSize > 0 ? (
            <p className={`mt-1 text-xs ${accentTextClass}`}>{(fileSize / 1024).toFixed(1)} KB</p>
          ) : null}
          <DocumentReviewMetadata
            uploadedAt={uploadedAt}
            uploadedBy={uploadedBy}
            status={status}
            reviewedAt={reviewedAt}
            reviewedBy={reviewedBy}
            rejectionReason={rejectionReason}
            reclassifiedFrom={reclassifiedFrom}
            reclassifiedAt={reclassifiedAt}
            reclassifiedBy={reclassifiedBy}
          />
        </div>
        <div className="ml-2 flex flex-shrink-0 items-center gap-1 max-[511px]:ml-0 max-[511px]:w-full max-[511px]:flex-wrap">
          <button
            type="button"
            onClick={() => onStatusChange('approved')}
            disabled={approveDisabled}
            className={`inline-flex items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              status === 'approved'
                ? 'bg-green-600 text-white'
                : 'border border-green-300 bg-white text-green-700 hover:bg-green-50'
            } disabled:cursor-not-allowed disabled:opacity-50`}
            aria-busy={busy && pendingAction === 'approve'}
          >
            {busy && pendingAction === 'approve' ? <ActionButtonSpinner size={12} /> : null}
            {busy && pendingAction === 'approve' ? 'Approving…' : 'Approve'}
          </button>
          <button
            type="button"
            onClick={() => setShowRejectionModal(true)}
            disabled={actionsDisabled}
            className={`inline-flex items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              isIncomplete
                ? 'bg-red-600 text-white'
                : 'border border-red-300 bg-white text-red-700 hover:bg-red-50'
            } disabled:cursor-not-allowed disabled:opacity-50`}
            aria-busy={busy && pendingAction === 'incomplete'}
          >
            {busy && pendingAction === 'incomplete' ? <ActionButtonSpinner size={12} /> : null}
            {busy && pendingAction === 'incomplete' ? 'Saving…' : rejectLabel}
          </button>
          {reclassifyOptions && reclassifyOptions.length > 0 && onReclassify ? (
            <div className="relative" ref={reclassifyRef}>
              <button
                type="button"
                onClick={() => setShowReclassifyMenu(!showReclassifyMenu)}
                disabled={actionsDisabled}
                className="flex items-center gap-1 rounded-lg border border-blue-300 bg-white px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                title="Reclassify document"
                aria-busy={busy && pendingAction === 'reclassify'}
              >
                {busy && pendingAction === 'reclassify' ? (
                  <ActionButtonSpinner size={12} />
                ) : (
                  <RefreshCw size={12} />
                )}
                <span className="hidden sm:inline">
                  {busy && pendingAction === 'reclassify' ? 'Reclassifying…' : 'Reclassify'}
                </span>
              </button>
              {showReclassifyMenu ? (
                <div className="absolute right-0 top-full z-20 mt-1 max-h-72 w-72 max-w-[min(18rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <div className="border-b border-gray-100 px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                      Reclassify as...
                    </p>
                  </div>

                  {emptyOptions.length > 0 ? (
                    <>
                      {occupiedOptions.length > 0 ? (
                        <div className="px-3 pb-1 pt-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                            Empty slots
                          </p>
                        </div>
                      ) : null}
                      {emptyOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleReclassifySelect(option)}
                          className="w-full px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700"
                        >
                          {option.label}
                        </button>
                      ))}
                    </>
                  ) : null}

                  {occupiedOptions.length > 0 ? (
                    <>
                      <div
                        className={`px-3 pb-1 ${emptyOptions.length > 0 ? 'mt-1 border-t border-gray-100 pt-2' : 'pt-2'}`}
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">
                          Already has a file — will be replaced
                        </p>
                      </div>
                      {occupiedOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleReclassifySelect(option)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-amber-800 transition-colors hover:bg-amber-50"
                        >
                          <File size={11} className="flex-shrink-0 text-amber-500" />
                          <span>{option.label}</span>
                        </button>
                      ))}
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            onClick={handleViewClick}
            disabled={!canView}
            className={`rounded-lg p-1.5 transition-colors hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40 ${accentTextClass}`}
            title="View file"
          >
            <Eye size={15} />
          </button>
          {onRemoveFile ? (
            <button
              type="button"
              onClick={onRemoveFile}
              disabled={
                removeBusy || status === 'approved' || status === 'rejected'
              }
              aria-busy={removeBusy}
              className={cn(
                'rounded-lg p-1.5 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
                accentTextClass,
              )}
              title={
                removeBusy
                  ? 'Removing file…'
                  : status === 'approved' || status === 'rejected'
                    ? 'Locked after review'
                    : 'Delete file'
              }
            >
              {removeBusy ? (
                <ActionButtonSpinner size={15} className="text-red-600" />
              ) : (
                <Trash2 size={15} />
              )}
            </button>
          ) : null}
        </div>
      </div>

      <ReclassifyReplaceExistingFileDialog
        isOpen={pendingReclassify != null}
        slotLabel={pendingReclassify?.label ?? ''}
        onCancel={() => setPendingReclassify(null)}
        onConfirm={handleConfirmReclassify}
      />

      <RejectionReasonModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onSubmit={handleRejectSubmit}
        documentName={fileName || 'Document'}
        predefinedReasons={incompleteReasons}
      />
    </>
  )
}
